import React, { useState } from 'react';
import {
  Box,
  Paper,
  Toolbar,
  IconButton,
  Tooltip,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import {
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  BrightnessAuto as AutoModeIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Dashboard as DashboardIcon,
  LocalHospital as DiseaseIcon,
  MedicalServices as TreatmentIcon,
  ModelTraining as TrainingSampleIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Logo } from '../common/Logo';
import SearchButton from '../SearchButton';
import { useNavigate, useLocation } from 'react-router-dom';

interface NavbarProps {
  isMobile: boolean;
  onMenuToggle: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ isMobile, onMenuToggle }) => {
  const { user, signOut } = useAuth();
  const { mode, resolvedMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Diseases', icon: <DiseaseIcon />, path: '/diseases' },
    { text: 'Treatments', icon: <TreatmentIcon />, path: '/treatments' },
    { text: 'Training', icon: <TrainingSampleIcon />, path: '/training-samples' },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
      setMobileMenuAnchor(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 48px)', md: 'calc(100% - 48px)' },
        // maxWidth: 1200,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          backgroundColor: (theme) => theme.palette.background.paper + 'F2', // 95% opacity
          backdropFilter: 'blur(20px)',
          borderRadius: 4,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          boxShadow: (theme) => theme.palette.mode === 'light' 
            ? '0 8px 32px rgba(0,0,0,0.08)'
            : '0 8px 32px rgba(255,255,255,0.08)',
        }}
      >
        <Toolbar 
          sx={{ 
            justifyContent: 'space-between',
            minHeight: { xs: 56, sm: 64 },
            px: { xs: 5, sm: 3 },
          }}
        >
          {/* Mobile Layout: Logo left, Menu right */}
          {isMobile ? (
            <>
              {/* Logo on Left */}
              <Logo size="small" showText />
              
              {/* Hamburger Menu on Right */}
              <IconButton
                onClick={handleMobileMenuOpen}
                sx={{ 
                  color: (theme) => theme.palette.text.primary,
                  p: 1.5,
                  borderRadius: 2,
                  transition: 'all 200ms ease-in-out',
                  transform: Boolean(mobileMenuAnchor) ? 'rotate(90deg)' : 'rotate(0deg)',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.action.hover,
                    transform: Boolean(mobileMenuAnchor) ? 'rotate(90deg) scale(1.1)' : 'rotate(0deg) scale(1.1)',
                  }
                }}
              >
                <MenuIcon sx={{ 
                  transition: 'transform 200ms ease-in-out',
                }} />
              </IconButton>
              
              {/* Mobile Dropdown Menu */}
              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 250,
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    bgcolor: (theme) => theme.palette.background.paper + 'F8', // 97% opacity
                    backdropFilter: 'blur(12px)',
                  }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                TransitionProps={{
                  style: {
                    transitionDuration: '200ms',
                    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                  }
                }}
                slotProps={{
                  root: {
                    slotProps: {
                      backdrop: {
                        sx: {
                          opacity: 0,
                          transition: 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                        }
                      }
                    }
                  }
                }}
              >
                {/* User Info */}
                {user && (
                  <>
                    <MenuItem 
                      sx={{ 
                        py: 2, 
                        cursor: 'default',
                        transition: 'all 150ms ease-in-out',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        }
                      }}
                    >
                      <ListItemIcon>
                        <Avatar sx={{ 
                          width: 32, 
                          height: 32, 
                          bgcolor: (theme) => theme.palette.primary.main,
                          fontSize: 14,
                          fontWeight: 600,
                          transition: 'transform 150ms ease-in-out',
                        }}>
                          {user.username?.charAt(0).toUpperCase() || 'U'}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={user.username || 'User'}
                        secondary={user.email}
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </MenuItem>
                    <Divider sx={{ opacity: 0.6 }} />
                  </>
                )}
                
                {/* Navigation Items */}
                {menuItems.map((item) => (
                  <MenuItem
                    key={item.text}
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      transition: 'all 150ms ease-in-out',
                      backgroundColor: location.pathname === item.path 
                        ? (theme) => `${theme.palette.primary.main}15`
                        : 'transparent',
                      borderLeft: location.pathname === item.path 
                        ? (theme) => `3px solid ${theme.palette.primary.main}`
                        : '3px solid transparent',
                      '&:hover': {
                        backgroundColor: (theme) => location.pathname === item.path 
                          ? `${theme.palette.primary.main}25`
                          : theme.palette.action.hover,
                        transform: 'translateX(4px)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <Box sx={{ 
                        color: location.pathname === item.path 
                          ? (theme) => theme.palette.primary.main
                          : (theme) => theme.palette.text.secondary,
                        transition: 'color 150ms ease-in-out',
                      }}>
                        {item.icon}
                      </Box>
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: location.pathname === item.path ? 600 : 500,
                        color: location.pathname === item.path ? 'primary.main' : 'text.primary'
                      }}
                    />
                  </MenuItem>
                ))}
                
                <Divider sx={{ opacity: 0.6, my: 1 }} />
                
                {/* Search */}
                <MenuItem 
                  onClick={handleMobileMenuClose}
                  sx={{
                    transition: 'all 150ms ease-in-out',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.action.hover,
                      transform: 'translateX(4px)',
                    }
                  }}
                >
                  <ListItemIcon>
                    <SearchIcon sx={{ 
                      transition: 'color 150ms ease-in-out',
                      color: (theme) => theme.palette.text.secondary,
                    }} />
                  </ListItemIcon>
                  <ListItemText primary="Search" />
                </MenuItem>
                
                {/* Theme Toggle */}
                <MenuItem 
                  onClick={() => {
                    toggleTheme();
                    handleMobileMenuClose();
                  }}
                  sx={{
                    transition: 'all 150ms ease-in-out',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.action.hover,
                      transform: 'translateX(4px)',
                    }
                  }}
                >
                  <ListItemIcon>
                    <Box sx={{ 
                      transition: 'transform 200ms ease-in-out',
                      '&:hover': { transform: 'rotate(180deg)' }
                    }}>
                      {mode === 'light' ? 
                        <DarkModeIcon sx={{ color: (theme) => theme.palette.text.secondary }} /> : 
                        mode === 'dark' ?
                        <AutoModeIcon sx={{ color: (theme) => theme.palette.text.secondary }} /> :
                        <LightModeIcon sx={{ color: (theme) => theme.palette.text.secondary }} />
                      }
                    </Box>
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      mode === 'light' ? 'Switch to Dark Mode' :
                      mode === 'dark' ? 'Switch to Auto Mode' :
                      'Switch to Light Mode'
                    }
                    secondary={mode === 'system' ? `Currently ${resolvedMode}` : undefined}
                  />
                </MenuItem>
                
                <Divider sx={{ opacity: 0.6 }} />
                
                {/* Sign Out */}
                {user && (
                  <MenuItem 
                    onClick={handleSignOut} 
                    sx={{ 
                      color: 'error.main',
                      transition: 'all 150ms ease-in-out',
                      '&:hover': {
                        backgroundColor: (theme) => `${theme.palette.error.main}08`,
                        transform: 'translateX(4px)',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <LogoutIcon sx={{ 
                        color: 'error.main',
                        transition: 'transform 150ms ease-in-out',
                      }} />
                    </ListItemIcon>
                    <ListItemText primary="Sign Out" />
                  </MenuItem>
                )}
              </Menu>
            </>
          ) : (
            /* Desktop/Tablet Layout */
            <>
              {/* Left Section */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Logo size="medium" showText />
              </Box>
              
              {/* Right Section */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SearchButton />
                
                <Tooltip title={
                  mode === 'light' ? 'Switch to dark mode' :
                  mode === 'dark' ? 'Switch to auto mode' :
                  'Switch to light mode'
                }>
                  <IconButton 
                    onClick={toggleTheme} 
                    size="small"
                    sx={{ 
                      color: (theme) => theme.palette.text.secondary,
                      '&:hover': { backgroundColor: (theme) => theme.palette.action.hover }
                    }}
                  >
                    {mode === 'light' ? <DarkModeIcon /> : 
                     mode === 'dark' ? <AutoModeIcon /> : 
                     <LightModeIcon />}
                  </IconButton>
                </Tooltip>
                
                {user && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                    <Avatar sx={{ 
                      width: 32, 
                      height: 32, 
                      bgcolor: (theme) => theme.palette.primary.main,
                      color: (theme) => theme.palette.primary.contrastText,
                      fontSize: 14,
                      fontWeight: 600,
                    }}>
                      {user.username?.charAt(0).toUpperCase() || 'U'}
                    </Avatar>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        color: (theme) => theme.palette.text.secondary
                      }}
                    >
                      {user.email || user.username}
                    </Typography>
                    <IconButton
                      onClick={handleSignOut}
                      size="small"
                      sx={{ 
                        color: (theme) => theme.palette.text.secondary,
                        ml: 0.5,
                        '&:hover': { backgroundColor: (theme) => theme.palette.action.hover }
                      }}
                    >
                      <LogoutIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Toolbar>
      </Paper>
    </Box>
  );
};